import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, formatCZK, formatDateTime, formatQty } from "@/components/Shared";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeftRight, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import { packageSize, packageLabel, toPieces, formatQtyWithPackage, productUnitOptions } from "@/lib/units";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const STATUS_LABEL = {
  pending: "Čeká na přijetí",
  received: "Přijato",
  received_with_issue: "Přijato s výhradou",
};

const STATUS_COLOR = {
  pending: { bg: "#C29329", soft: "#C293291A" },
  received: { bg: "#376A4D", soft: "#376A4D1A" },
  received_with_issue: { bg: "#9E4747", soft: "#9E47471A" },
};

export default function Transfers() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [fromBranch, setFromBranch] = useState(user.role === "admin" ? "" : user.branch_id || "");
  const [sourceStock, setSourceStock] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedProd, setSelectedProd] = useState(null);
  const [toBranch, setToBranch] = useState("");
  const [qty, setQty] = useState(0);
  const [unitMode, setUnitMode] = useState("piece");
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    const [b, p, t] = await Promise.all([api.get("/branches"), api.get("/products", { params: { include_deleted: true } }), api.get("/transfers")]);
    setBranches(b.data);
    setProducts(p.data);
    setTransfers(t.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    (async () => {
      if (!fromBranch) {
        setSourceStock([]);
        return;
      }
      try {
        const { data } = await api.get("/branch-stock", { params: { branch_id: fromBranch } });
        setSourceStock(data);
      } catch {
        setSourceStock([]);
      }
    })();
  }, [fromBranch, transfers]);

  const prodName = (id) => products.find((p) => p.id === id)?.name || "—";
  const brName = (id) => branches.find((b) => b.id === id)?.name || "—";

  const visibleStock = useMemo(() => {
    const activeStock = sourceStock.filter((s) => {
      const p = products.find((pp) => pp.id === s.product_id);
      return p && !p.deleted;
    });
    const q = search.trim().toLowerCase();
    if (!q) return activeStock;
    return activeStock.filter((s) => prodName(s.product_id).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceStock, search, products]);

  const openTile = (item) => {
    const product = products.find((p) => p.id === item.product_id);
    setSelectedProd({ product, available: item });
    setUnitMode(product?.default_dispatch_unit || "piece");
    setQty(0);
    setToBranch("");
    setOpen(true);
  };

  const submit = async () => {
    if (!selectedProd) return;
    const count = parseFloat(qty);
    if (!count || count <= 0) return toast.error("Zadejte množství");
    if (!toBranch) return toast.error("Vyberte cílovou pobočku");
    const pieces = toPieces(selectedProd.product, unitMode, count);
    if (pieces > selectedProd.available.quantity + 1e-9) return toast.error("Více než dispozice");
    setSubmitting(true);
    try {
      await api.post("/transfers", {
        from_branch_id: fromBranch,
        to_branch_id: toBranch,
        product_id: selectedProd.product.id,
        quantity: pieces,
        unit_mode: unitMode,
        unit_count: count,
      });
      toast.success("Převod vytvořen — čeká na potvrzení příjmu");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="transfers-page">
      <PageHeader
        eyebrow="Logistika mezi pobočkami"
        title="Převod mezi pobočkami"
        description="Vyberte zdrojovou pobočku, klikněte na výrobek a zadejte cílovou pobočku + množství. Vyskladnění proběhne až po potvrzení příjmu na cílové pobočce (Přijato / Přijato s výhradou)."
      />

      <div className="space-y-6">
        <div className="card-cafe p-4 flex flex-wrap items-center gap-3">
          <span className="text-sm text-[#57534E]">Zdrojová pobočka:</span>
          {user.role === "admin" ? (
            <select
              data-testid="transfer-from-select"
              className="input-cafe py-1.5"
              value={fromBranch}
              onChange={(e) => setFromBranch(e.target.value)}
            >
              <option value="">— vyberte —</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          ) : (
            <span className="font-medium">{brName(user.branch_id)}</span>
          )}
        </div>

        {fromBranch && (
          <div className="card-cafe p-6">
            <div className="label-eyebrow mb-3">Výrobky k převodu</div>
            <SearchBar value={search} onChange={setSearch} placeholder="Hledat výrobek…" testid="transfer-search" />
            {visibleStock.length === 0 ? (
              <div className="text-sm text-[#57534E] py-8 text-center border border-dashed border-[#E5E1D8] rounded-md">
                Žádné výrobky na zdrojové pobočce.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-5">
                {visibleStock.map((s) => {
                  const product = products.find((p) => p.id === s.product_id);
                  return (
                    <button
                      key={s.product_id}
                      data-testid={`transfer-tile-${s.product_id}`}
                      onClick={() => openTile(s)}
                      className="group flex flex-col items-center justify-center p-6 bg-white border-2 border-[#E5E1D8] rounded-2xl hover:border-[#D4A373] hover:-translate-y-1 hover:shadow-md transition-all"
                    >
                      <div className="w-14 h-14 rounded-full bg-[#F9F6F0] grid place-items-center mb-3 group-hover:bg-[#D4A373]/25">
                        <ArrowLeftRight className="w-6 h-6 text-[#3A2D27]" />
                      </div>
                      <div className="text-base font-medium text-center">{prodName(s.product_id)}</div>
                      <div className="text-xs text-[#376A4D] font-medium mt-1 text-center">
                        {formatQtyWithPackage(s.quantity, product, "ks")}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="card-cafe p-6">
          <div className="label-eyebrow mb-4">Historie převodů</div>
          {transfers.length === 0 ? (
            <div className="text-sm text-[#57534E] py-6 text-center">Žádné převody.</div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {transfers.map((t) => {
                  const status = t.status || "pending";
                  const colors = STATUS_COLOR[status];
                  const product = products.find((p) => p.id === t.product_id);
                  return (
                    <div key={t.id} data-testid={`transfer-card-${t.id}`} className="border border-[#E5E1D8] rounded-md p-3 bg-white">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-base truncate">{prodName(t.product_id)}</div>
                          <div className="text-xs text-[#57534E] mt-0.5">{formatDateTime(t.created_at)}</div>
                        </div>
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shrink-0"
                          style={{ background: colors.soft, color: colors.bg }}
                        >
                          {status === "pending" && <Clock className="w-3 h-3" />}
                          {status === "received" && <CheckCircle2 className="w-3 h-3" />}
                          {status === "received_with_issue" && <AlertTriangle className="w-3 h-3" />}
                          {STATUS_LABEL[status]}
                        </span>
                      </div>
                      <div className="text-sm text-[#57534E]">
                        <span className="font-medium text-[#1C1917]">{brName(t.from_branch_id)}</span>
                        {" → "}
                        <span className="font-medium text-[#1C1917]">{brName(t.to_branch_id)}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
                        <div>
                          <div className="text-xs text-[#57534E]">Odesláno</div>
                          <div className="mono font-medium">{formatQtyWithPackage(t.quantity, product, "ks")}</div>
                        </div>
                        <div>
                          <div className="text-xs text-[#57534E]">Přijato</div>
                          <div className="mono font-medium">
                            {status === "pending" ? "—" : formatQtyWithPackage(t.received_quantity || 0, product, "ks")}
                          </div>
                        </div>
                      </div>
                      {status === "received_with_issue" && t.discrepancy_note && (
                        <div className="mt-2 text-xs text-[#9E4747] border-t border-[#E5E1D8] pt-2">
                          Rozpor: {t.discrepancy_note}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto rounded-md border border-[#E5E1D8]">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F6F0]">
                  <tr className="text-left">
                    <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Čas</th>
                    <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Z pobočky</th>
                    <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Na pobočku</th>
                    <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Výrobek</th>
                    <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">Odesláno</th>
                    <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">Přijato</th>
                    <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium text-right">Náklad</th>
                    <th className="p-3 text-xs uppercase tracking-[0.1em] text-[#57534E] font-medium">Stav</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.map((t) => {
                    const status = t.status || "pending";
                    const colors = STATUS_COLOR[status];
                    const product = products.find((p) => p.id === t.product_id);
                    return (
                      <tr key={t.id} className="border-t border-[#E5E1D8]">
                        <td className="p-3">{formatDateTime(t.created_at)}</td>
                        <td className="p-3">{brName(t.from_branch_id)}</td>
                        <td className="p-3 font-medium">{brName(t.to_branch_id)}</td>
                        <td className="p-3">{prodName(t.product_id)}</td>
                        <td className="p-3 text-right">{formatQtyWithPackage(t.quantity, product, "ks")}</td>
                        <td className="p-3 text-right">
                          {status === "pending" ? "—" : formatQtyWithPackage(t.received_quantity || 0, product, "ks")}
                        </td>
                        <td className="p-3 text-right">{status === "pending" ? "—" : formatCZK(t.total_cost)}</td>
                        <td className="p-3">
                          <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{ background: colors.soft, color: colors.bg }}
                          >
                            {status === "pending" && <Clock className="w-3 h-3" />}
                            {status === "received" && <CheckCircle2 className="w-3 h-3" />}
                            {status === "received_with_issue" && <AlertTriangle className="w-3 h-3" />}
                            {STATUS_LABEL[status]}
                          </span>
                          {status === "received_with_issue" && t.discrepancy_note && (
                            <div className="text-xs text-[#9E4747] mt-1">Rozpor: {t.discrepancy_note}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-white border-[#E5E1D8]" data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>
              Převod — {selectedProd?.product?.name}
            </DialogTitle>
            <DialogDescription>
              Z pobočky <strong>{brName(fromBranch)}</strong>. Dispozice:{" "}
              {selectedProd && formatQtyWithPackage(selectedProd.available.quantity, selectedProd.product, "ks")}
            </DialogDescription>
          </DialogHeader>

          {selectedProd && (
            <div className="space-y-3 pt-1">
              <label className="block">
                <div className="text-sm font-medium mb-1.5">Cílová pobočka</div>
                <select
                  data-testid="transfer-to-select"
                  className="input-cafe w-full"
                  value={toBranch}
                  onChange={(e) => setToBranch(e.target.value)}
                >
                  <option value="">— vyberte —</option>
                  {branches.filter((b) => b.id !== fromBranch).map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </label>
              {selectedProd && productUnitOptions(selectedProd.product).length > 1 && (
                <div>
                  <div className="text-sm font-medium mb-1.5">Jednotka</div>
                  <div className="inline-flex flex-wrap rounded-md border border-[#E5E1D8] overflow-hidden">
                    {productUnitOptions(selectedProd.product).map((o) => (
                      <button
                        key={o.value}
                        data-testid={`transfer-unit-${o.value}`}
                        type="button"
                        onClick={() => setUnitMode(o.value)}
                        className={`px-4 py-2 text-sm ${unitMode === o.value ? "bg-[#3A2D27] text-white" : "bg-white text-[#3A2D27]"}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <label className="block">
                <div className="text-sm font-medium mb-1.5">Množství</div>
                <input
                  data-testid="transfer-qty"
                  autoFocus
                  type="number"
                  inputMode="numeric"
                  min="0"
                  step="1"
                  className="input-cafe w-full text-xl"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                />
                {unitMode !== "piece" && parseFloat(qty) > 0 && (
                  <div className="text-xs text-[#57534E] mt-1">
                    = {toPieces(selectedProd.product, unitMode, qty)} ks
                  </div>
                )}
              </label>
            </div>
          )}

          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]">
              Zrušit
            </button>
            <button data-testid="transfer-confirm" onClick={submit} disabled={submitting} className="btn-primary">
              {submitting ? "Ukládám…" : "Vytvořit převod"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
